import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../services/api';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Typography, Autocomplete } from '@mui/material';
import './EditDialog.css';

const AddIcon = apiService.getBlobAssetUrl('Add_Icon.svg');
const DeleteIcon = apiService.getBlobAssetUrl('delete_A1.svg');

// Helper function to parse and format dates like "Month Day, Year"
function parseProjectDate(dateString) {
  if (!dateString) return '';
  try {
    const dateParts = dateString.split(' ');
    const month = new Date(Date.parse(dateParts[0] + ' 1, 2000')).getMonth();
    const day = parseInt(dateParts[1].replace(',', ''), 10);
    const year = parseInt(dateParts[2], 10);
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  } catch (error) {
    return '';
  }
}

// Helper function to process customer contacts
function processCustomerContacts(fullNames, emails) {
  const nameArray = fullNames ? fullNames.split(', ') : [];
  const emailArray = emails ? emails.split(', ') : [];
  const contactPairs = nameArray.map((name, index) => `${name}||${emailArray[index] || ''}`);
  const uniqueContacts = Array.from(new Set(contactPairs)).map((entry, index) => {
    const [fullName, email] = entry.split('||');
    return { id: index + 1, fullName, email };
  });
  return uniqueContacts.length > 0 ? uniqueContacts : [{ id: 1, fullName: '', email: '' }];
}

// Helper function to normalize arrays and validate fields
function normalizeAndValidate(project, statuses, types, practices, clients) {
  const normalizedStatuses = statuses.map((status, index) =>
    typeof status === 'string' ? { ID: index + 1, Name: status } : { ID: status.ID || index + 1, Name: status.Name }
  );
  const normalizedTypes = types.map((type, index) =>
    typeof type === 'string' ? { ID: index + 1, Name: type } : { ID: type.ID || index + 1, Name: type.Name }
  );
  const normalizedClients = clients.map((client, index) =>
    typeof client === 'string' ? { ID: index + 1, Name: client } : { ID: client.ID || index + 1, Name: client.Name }
  );
  const validStatus = normalizedStatuses.find((s) => s.Name === project.Status)?.Name || '';
  const validType = normalizedTypes.find((t) => t.Name === project.Type)?.Name || '';
  
  // Map practice by ID if Practice is a unique ID, otherwise by Name
  let validPractice = '';
  // First try to match by ID
  const practiceById = practices.find((p) => String(p.ID) === String(project.Practice));
  if (practiceById) {
    validPractice = practiceById.Name;
  } else {
    // If not found by ID, try to match by Name
    validPractice = practices.find((p) => p.Name === project.Practice)?.Name || '';
  }
  
  const validClient = normalizedClients.find((c) => 
    c.Name.toLowerCase() === (project.Client || '').toLowerCase()
  )?.Name || (project.Client || '');
  return { normalizedStatuses, normalizedTypes, validStatus, validType, validPractice, validClient, normalizedClients };
}

const EditDialog = ({ project, statuses, types, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    projectName: '',
    projectStatus: '',
    projectType: '',
    projectStartDate: '',
    projectEndDate: '',
    projectDescription: '',
    projectPractice: '',
    projectClient: '',
    customerContacts: [{ id: 1, fullName: '', email: '' }],
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [practices, setPractices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [nameError, setNameError] = useState(null);
  const [contactErrors, setContactErrors] = useState({});
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);

  // Fetch practices and clients
  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const response = await apiService.fetchData('teams');
        if (response.status === 'success' && Array.isArray(response.results)) {
          setPractices(response.results);
        } else {
          setPractices([]);
          setErrors((prev) => ({ ...prev, form: 'Failed to load practices: Invalid response format.' }));
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, form: 'Failed to load practices.' }));
      }
    };

    const fetchClients = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await apiService.fetchData('clients/details', { signal: controller.signal });
        clearTimeout(timeoutId);
        let clientsData = [];
        if (response.status === 'success' && Array.isArray(response.results)) {
          clientsData = response.results;
        } else if (Array.isArray(response.data)) {
          clientsData = response.data.map((item) => ({ Name: item.name || item.Name }));
        } else if (Array.isArray(response)) {
          clientsData = response.map((item) => ({ Name: item.name || item.Name }));
        } else {
          setErrors((prev) => ({ ...prev, form: 'Failed to load clients: Invalid response format.' }));
        }
        setClients(clientsData);
      } catch (err) {
        setErrors((prev) => ({ ...prev, form: 'Failed to load clients: ' + err.message }));
      } finally {
        setLoadingClients(false);
      }
    };

    fetchPractices();
    fetchClients();
  }, []);

  // Initialize form data when project and clients are available
  useEffect(() => {
    if (project && !loadingClients && practices.length > 0) {
      console.log('=== EDIT PROJECT DATA ===');
      console.log('Project:', project);
      console.log('Clients:', clients);
      console.log('Practices:', practices);
      
      const formattedStartDate = parseProjectDate(project['Start Date']);
      const formattedEndDate = parseProjectDate(project['End Date']);
      const customerContacts = processCustomerContacts(project.FullName, project.Email);
      const { validStatus, validType, validPractice, validClient } = normalizeAndValidate(project, statuses, types, practices, clients);
      
      console.log('Formatted Start Date:', formattedStartDate);
      console.log('Formatted End Date:', formattedEndDate);
      console.log('Customer Contacts:', customerContacts);
      console.log('Valid Client:', validClient);
      console.log('Valid Practice:', validPractice);
      console.log('========================');
      
      setFormData({
        projectName: project['Project Name'] || '',
        projectStatus: validStatus,
        projectType: validType,
        projectStartDate: formattedStartDate,
        projectEndDate: formattedEndDate,
        projectDescription: project.Description || '',
        projectPractice: validPractice || project.Practice || '',
        projectClient: validClient || project.Client || '',
        customerContacts,
      });
      
      // Store initial data for change tracking
      setInitialFormData({
        projectName: project['Project Name'] || '',
        projectStatus: validStatus,
        projectType: validType,
        projectStartDate: formattedStartDate,
        projectEndDate: formattedEndDate,
        projectDescription: project.Description || '',
        projectPractice: validPractice || project.Practice || '',
        projectClient: validClient || project.Client || '',
        customerContacts: JSON.stringify(customerContacts),
      });
    }
  }, [project, statuses, types, practices, clients, loadingClients]);

  // Clear name error on dialog close
  useEffect(() => {
    return () => {
      setNameError(null);
    };
  }, []);

  const validateDatesByStatus = (status, startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    setStartDateError(null);
    setEndDateError(null);

    if (!status) {
      if (!startDate) setStartDateError('Start date is required.');
      if (!endDate) setEndDateError('End date is required.');
      return false;
    }

    if (!start) {
      setStartDateError('Start date is required.');
      return false;
    }
    if (!end) {
      setEndDateError('End date is required.');
      return false;
    }
    if (end <= start) {
      setEndDateError('End date must be after start date.');
      return false;
    }

    const todayFormatted = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const statusLower = status.toLowerCase();

    if (statusLower === 'in progress') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for In Progress projects.`);
        return false;
      }
      if (end <= today) {
        setEndDateError(`End date must be after today (${todayFormatted}) for In Progress projects.`);
        return false;
      }
    } else if (statusLower === 'completed') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for Completed projects.`);
        return false;
      }
      if (end >= today) {
        setEndDateError(`End date must be before today (${todayFormatted}) for Completed projects.`);
        return false;
      }
    } else if (statusLower === 'cancelled') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for Cancelled projects.`);
        return false;
      }
    }

    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project Name is required';
    }
    if (!formData.projectStatus) {
      newErrors.projectStatus = 'Project Status is required';
    }
    if (!formData.projectType) {
      newErrors.projectType = 'Project Type is required';
    }
    if (!formData.projectStartDate) {
      newErrors.projectStartDate = 'Project Start Date is required';
    }
    if (formData.projectStartDate && !formData.projectEndDate) {
      newErrors.projectEndDate = 'End date is required when a start date is selected.';
    }
    if (!formData.projectPractice) {
      newErrors.projectPractice = 'Project Practice is required';
    }
    if (!formData.projectClient) {
      newErrors.projectClient = 'Client is required';
    }

    if (!validateDatesByStatus(formData.projectStatus, formData.projectStartDate, formData.projectEndDate)) {
      return false;
    }

    formData.customerContacts.forEach((contact, index) => {
      if (contact.fullName.trim() && !contact.email.trim()) {
        newErrors[`email-${contact.id}`] = `Email is required for Contact ${index + 1}`;
      }
      if (!contact.fullName.trim() && contact.email.trim()) {
        newErrors[`contact-${contact.id}`] = `Contact name is required for Email ${index + 1}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddNew = () => {
    setFormData((prev) => ({
      ...prev,
      customerContacts: [
        ...prev.customerContacts,
        { id: prev.customerContacts.length + 1, fullName: '', email: '' },
      ],
    }));
  };

  const handleRemoveContact = (id) => {
    setFormData((prev) => ({
      ...prev,
      customerContacts: prev.customerContacts.filter((contact) => contact.id !== id),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`contact-${id}`];
      delete newErrors[`email-${id}`];
      return newErrors;
    });
  };

  const handleCustomerChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      customerContacts: prev.customerContacts.map((contact) =>
        contact.id === id ? { ...contact, [field]: value } : contact
      ),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${field}-${id}`];
      return newErrors;
    });

    const contactIndex = formData.customerContacts.findIndex((c) => c.id === id);
    const updatedContact = { ...formData.customerContacts[contactIndex], [field]: value };

    const currentErrors = contactErrors[contactIndex] || {};

    if (field === 'fullName') {
      const trimmedValue = value.trim();
      const namePattern = /^[a-zA-Z0-9\s_-]+$/;
      if (!trimmedValue) {
        currentErrors.fullName = 'Full Name cannot be empty.';
      } else if (!namePattern.test(trimmedValue)) {
        currentErrors.fullName = 'Full Name can only contain letters, numbers, spaces, hyphens, or underscores.';
      } else {
        currentErrors.fullName = null;
        if (updatedContact.email && updatedContact.fullName) {
          const isDuplicate = formData.customerContacts.some((contact, i) =>
            i !== contactIndex && contact.fullName.trim() === trimmedValue && contact.email === updatedContact.email
          );
          if (isDuplicate) {
            currentErrors.duplicate = 'A contact with this name and email already exists.';
          } else {
            currentErrors.duplicate = null;
          }
        }
      }
    } else if (field === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailPattern.test(value)) {
        currentErrors.email = 'Please enter a valid email address (e.g., user@example.com).';
      } else {
        currentErrors.email = null;
        if (updatedContact.fullName && updatedContact.email) {
          const isDuplicate = formData.customerContacts.some((contact, i) =>
            i !== contactIndex && contact.fullName.trim() === updatedContact.fullName.trim() && contact.email === updatedContact.email
          );
          if (isDuplicate) {
            currentErrors.duplicate = 'A contact with this name and email already exists.';
          } else {
            currentErrors.duplicate = null;
          }
        }
      }
    }

    setContactErrors((prev) => ({
      ...prev,
      [contactIndex]: currentErrors.fullName || currentErrors.email || currentErrors.duplicate ? currentErrors : null,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    
    // Fetch client contacts when client changes
    if (field === 'projectClient' && value) {
      const fetchClientContacts = async () => {
        try {
          const response = await apiService.fetchData(`clients/${value}`);
          if (Array.isArray(response) && response.length > 0) {
            const clientContacts = response.map((contact, index) => ({
              id: Date.now() + index,
              fullName: contact.OwnerName || '',
              email: contact.OwnerEmail || ''
            }));
            setFormData((prev) => ({ ...prev, customerContacts: clientContacts }));
            setContactErrors({});
          }
        } catch (err) {
          console.error('Error fetching client contacts:', err);
        }
      };
      fetchClientContacts();
    }
    
    if (field === 'projectName') {
      const trimmedValue = value.trim();
      const validPattern = /^[a-zA-Z0-9\s_-]+$/;
      if (!trimmedValue) {
        setNameError('Project name can\'t be empty.');
      } else if (!validPattern.test(trimmedValue)) {
        setNameError('Special characters (e.g., !@#$%^&*()) are not allowed in the project name.');
      } else {
        setNameError(null);
      }
    }

    if (field === 'projectStartDate' || field === 'projectEndDate' || field === 'projectStatus') {
      const newStatus = field === 'projectStatus' ? value : formData.projectStatus;
      const newStartDate = field === 'projectStartDate' ? value : formData.projectStartDate;
      const newEndDate = field === 'projectEndDate' ? value : formData.projectEndDate;

      if (field === 'projectStartDate' && value && formData.projectEndDate) {
        const start = new Date(value);
        const end = new Date(formData.projectEndDate);
        if (end <= start) {
          setFormData((prev) => ({ ...prev, [field]: value, projectEndDate: '' }));
          return;
        }
      }

      validateDatesByStatus(newStatus, newStartDate, newEndDate);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    if (submitting) {
      return;
    }
    setSubmitting(true);

    try {
      const updatedContacts = formData.customerContacts
        .filter((contact) => contact.fullName.trim() && contact.email.trim())
        .map((contact) => ({
          full_name: contact.fullName.trim(),
          email: contact.email.trim(),
        }));

      const selectedStatus = projectStatusOptions.find((status) => status.Name === formData.projectStatus);
      const normalizedTypes = types.map((type, index) =>
        typeof type === 'string' ? { ID: index + 1, Name: type } : { ID: type.ID || index + 1, Name: type.Name }
      );
      const normalizedPractices = practices.map((practice, index) =>
        typeof practice === 'string' ? { ID: index + 1, Name: practice } : { ID: practice.ID || index + 1, Name: practice.Name }
      );
      const normalizedClients = clients.map((client, index) =>
        typeof client === 'string' ? { ID: index + 1, Name: client } : { ID: client.ID || index + 1, Name: client.Name }
      );


      const selectedType = normalizedTypes.find((type) => type.Name === formData.projectType);
      const selectedPractice = normalizedPractices.find((practice) => practice.Name === formData.projectPractice);

      if (nameError) {
        throw new Error(nameError);
      }

      // Check for invalid emails or full names
      const invalidContacts = formData.customerContacts.some((contact, index) =>
        contact.email && contactErrors[index]?.email ||
        contact.fullName && contactErrors[index]?.fullName
      );
      if (invalidContacts) {
        throw new Error('Please fix all invalid contact details before saving.');
      }

      // Check for duplicate contacts with trimmed values
      const seen = new Set();
      const hasDuplicates = formData.customerContacts.some((contact) => {
        const key = `${contact.fullName.trim()}-${contact.email}`;
        if (seen.has(key) && contact.fullName && contact.email) {
          return true;
        }
        seen.add(key);
        return false;
      });
      if (hasDuplicates) {
        throw new Error('Duplicate contacts with the same name and email are not allowed.');
      }

      // Retrieve user_id from sessionStorage
      const userId = sessionStorage.getItem('user_id');

      const payload = {
        project_id: project.id,
        last_modified_by: userId || null,
        updated_fields: {
          project_name: formData.projectName || null,
          project_status: selectedStatus ? selectedStatus.ID : null,
          project_type: selectedType ? selectedType.ID : null,
          project_start_date: formData.projectStartDate || null,
          endDate: formData.projectEndDate || null,
          project_description: formData.projectDescription || null,
          practice_name: selectedPractice ? selectedPractice.Name : null,
          practice_id: selectedPractice ? selectedPractice.ID : null,
          client_name: formData.projectClient || null,
          project_customer_contacts: updatedContacts,
        },
      };

      // Create optimisticData for UI update
      const optimisticData = {
        projectName: formData.projectName,
        projectStatus: selectedStatus ? selectedStatus.Name : '',
        projectType: selectedType ? selectedType.Name : '',
        projectStartDate: formData.projectStartDate,
        projectEndDate: formData.projectEndDate,
        projectDescription: formData.projectDescription,
        projectPractice: selectedPractice ? selectedPractice.Name : '',
        projectClient: formData.projectClient,
        customerContacts: updatedContacts,
      };

      await onSave(payload, optimisticData);
      onClose();
    } catch (error) {
      setErrors({ form: 'Failed to save project: ' + error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const retryFetchClients = async () => {
    setErrors((prev) => ({ ...prev, form: '' }));
    setLoadingClients(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await apiService.fetchData('clients/details', { signal: controller.signal });
      clearTimeout(timeoutId);
      let clientsData = [];
      if (response.status === 'success' && Array.isArray(response.results)) {
        clientsData = response.results;
      } else if (Array.isArray(response.data)) {
        clientsData = response.data.map((item) => ({ Name: item.name || item.Name }));
      } else if (Array.isArray(response)) {
        clientsData = response.map((item) => ({ Name: item.name || item.Name }));
      } else {
        setErrors((prev) => ({ ...prev, form: 'Failed to load clients: Invalid response format.' }));
      }
      setClients(clientsData);
    } catch (err) {
      setErrors((prev) => ({ ...prev, form: 'Failed to load clients: ' + err.message }));
    } finally {
      setLoadingClients(false);
    }
  };

  // Check if form has changes
  const hasChanges = initialFormData && (
    formData.projectName !== initialFormData.projectName ||
    formData.projectStatus !== initialFormData.projectStatus ||
    formData.projectType !== initialFormData.projectType ||
    formData.projectStartDate !== initialFormData.projectStartDate ||
    formData.projectEndDate !== initialFormData.projectEndDate ||
    formData.projectDescription !== initialFormData.projectDescription ||
    formData.projectPractice !== initialFormData.projectPractice ||
    formData.projectClient !== initialFormData.projectClient ||
    JSON.stringify(formData.customerContacts) !== initialFormData.customerContacts
  );

  const projectStatusOptions = [
    { ID: 1, Name: 'In Progress' },
    { ID: 2, Name: 'Completed' },
    { ID: 3, Name: 'Cancelled' }
  ];
  const normalizedTypes = types.map((type, index) =>
    typeof type === 'string' ? { ID: index + 1, Name: type } : { ID: type.ID || index + 1, Name: type.Name }
  );
  const normalizedPractices = practices.sort((a, b) => a.Name.localeCompare(b.Name)).map((practice, index) =>
    typeof practice === 'string' ? { ID: index + 1, Name: practice } : { ID: practice.ID || index + 1, Name: practice.Name }
  );
  const normalizedClients = clients.map((client, index) =>
    typeof client === 'string' ? { ID: index + 1, Name: client } : { ID: client.ID || index + 1, Name: client.Name }
  );

  return (
    <Dialog open={!!project} onClose={onClose} maxWidth="md" fullWidth className="Project-dialog">
      <div className="Project-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
        <DialogTitle className="Project-dialog-title1" style={{ background: 'none', padding: 0, flex: 1 }}>Edit Project Details</DialogTitle>
        <button className="Project-close-button" onClick={onClose} style={{ marginLeft: 'auto' }}>
          ×
        </button>
      </div>
      <DialogContent className="Project-dialog-content">
        {errors.form && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography color="error">{errors.form}</Typography>
            {errors.form.includes('clients') && (
              <Button onClick={retryFetchClients} disabled={submitting || loadingClients}>
                Retry
              </Button>
            )}
          </div>
        )}
        <Autocomplete
          options={normalizedClients}
          getOptionLabel={(option) => option.Name || ''}
          value={normalizedClients.find((client) => client.Name === formData.projectClient) || (formData.projectClient ? { Name: formData.projectClient } : null)}
          onChange={(event, newValue) => handleChange('projectClient', newValue ? newValue.Name : '')}
          disabled={submitting || loadingClients}
          renderInput={(params) => (
            <TextField
              {...params}
              name="projectClient"
              label="Select Client"
              variant="outlined"
              fullWidth
              required
              error={!!errors.projectClient}
              helperText={errors.projectClient || (loadingClients ? 'Loading clients...' : clients.length === 0 ? 'No clients available' : '')}
              InputLabelProps={{
                shrink: true,
                style: { color: 'black' },
                required: true,
                classes: { asterisk: 'red-asterisk' },
              }}
              InputProps={{
                ...params.InputProps,
                style: { color: 'grey', fontSize: '15px' },
              }}
            />
          )}
          noOptionsText={loadingClients ? 'Loading...' : 'No clients found'}
          style={{ marginBottom: '20px' }}
        />

        {/* First Row: Project Name and Project Practice */}
        <div className="Project-project-details-row Project-project-details-row--double">
          <TextField
            name="projectName"
            label="Project Name"
            variant="outlined"
            fullWidth
            placeholder="Enter project name (e.g., Website Redesign)"
            value={formData.projectName}
            onChange={(e) => handleChange('projectName', e.target.value)}
            onBlur={async (e) => {
              const projectName = e.target.value.trim();
              if (projectName && projectName !== project['Project Name']) {
                try {
                  const userId = sessionStorage.getItem('user_id');
                  const PracticeId = sessionStorage.getItem('practice_id');
                  if (!userId) {
                    setNameError('User ID is not available. Please log in again.');
                    return;
                  }
                  const response = await apiService.fetchData(`projects/${userId}/${PracticeId}/survey`);
                  const projects = response.results || [];
                  const exists = projects.some(
                    (p) => p.Name.toLowerCase() === projectName.toLowerCase() && p.ID !== project.id
                  );
                  if (exists) {
                    setNameError('A project with this name already exists.');
                  } else {
                    setNameError(null);
                  }
                } catch (err) {
                  setNameError('Error checking project name. Please try again.');
                }
              } else {
                setNameError(null);
              }
            }}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting}
            error={!!errors.projectName || !!nameError}
            helperText={errors.projectName || nameError}
          />
          <TextField
            name="projectPractice"
            label="Project Practice"
            select
            variant="outlined"
            fullWidth
            value={formData.projectPractice}
            onChange={(e) => handleChange('projectPractice', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting || normalizedPractices.length === 0}
            error={!!errors.projectPractice}
            helperText={errors.projectPractice}
          >
            <option value="">Select Project Practice</option>
            {normalizedPractices.map((practice) => (
              <option key={practice.ID || `practice-${practice.Name}`} value={practice.Name}>
                {practice.Name}
              </option>
            ))}
          </TextField>
        </div>

        {/* Second Row: Project Status and Project Type */}
        <div className="Project-project-details-row Project-project-details-row--double">
          <TextField
            name="projectStatus"
            label="Project Status"
            select
            variant="outlined"
            fullWidth
            value={formData.projectStatus}
            onChange={(e) => handleChange('projectStatus', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting}
            error={!!errors.projectStatus || !!startDateError}
            helperText={errors.projectStatus}
          >
            <option value="">Select Project Status</option>
            {projectStatusOptions.map((status) => (
              <option key={status.ID} value={status.Name}>
                {status.Name}
              </option>
            ))}
          </TextField>
          <TextField
            name="projectType"
            label="Project Type"
            select
            variant="outlined"
            fullWidth
            value={formData.projectType}
            onChange={(e) => handleChange('projectType', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting}
            error={!!errors.projectType}
            helperText={errors.projectType}
          >
            <option value="">Select Project Type</option>
            {normalizedTypes.map((type) => (
              <option key={type.ID} value={type.Name}>
                {type.Name}
              </option>
            ))}
          </TextField>
        </div>

        {/* Third Row: Project Start Date and Project End Date */}
        <div className="Project-project-details-row Project-project-details-row--double">
          <TextField
            name="projectStartDate"
            label="Project Start Date"
            type="date"
            variant="outlined"
            fullWidth
            value={formData.projectStartDate}
            onChange={(e) => handleChange('projectStartDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting}
            error={!!errors.projectStartDate || !!startDateError}
            helperText={errors.projectStartDate || startDateError}
          />
          <TextField
            name="projectEndDate"
            label="Project End Date"
            type="date"
            variant="outlined"
            fullWidth
            value={formData.projectEndDate}
            onChange={(e) => handleChange('projectEndDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
              style: { color: 'black' },
              required: true,
              classes: { asterisk: 'red-asterisk' },
            }}
            InputProps={{
              style: { color: 'grey', fontSize: '15px' },
            }}
            required
            disabled={submitting}
            error={!!errors.projectEndDate || !!endDateError}
            helperText={errors.projectEndDate || endDateError}
          />
        </div>

        {/* Description */}
        <TextField
          name="projectDescription"
          label="Project Description"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          placeholder="Describe the project (e.g., goals, scope)"
          value={formData.projectDescription}
          onChange={(e) => handleChange('projectDescription', e.target.value)}
          InputLabelProps={{
            shrink: true,
            style: { color: 'black' },
            required: true,
            classes: { asterisk: 'red-asterisk' },
          }}
          InputProps={{
            style: { color: 'grey', fontSize: '15px' },
          }}
          required
          disabled={submitting}
          sx={{ mb: 3 }}
        />

        {/* Contacts */}
        <Typography variant="h6" className="Project-dialog-section-title" sx={{ mt: 2, mb: 2 }}>Contacts</Typography>
        {formData.customerContacts.length > 0 ? (
          formData.customerContacts.map((contact) => (
            <div className="Project-contact-row" key={contact.id}>
            <TextField
              name="fullName"
              label="Full Name"
              variant="outlined"
              fullWidth
              value={contact.fullName}
              onChange={(e) => handleCustomerChange(contact.id, 'fullName', e.target.value)}
              placeholder="Enter full name (e.g., John Doe)"
              InputLabelProps={{
                shrink: true,
                style: { color: 'black' },
                required: true,
                classes: { asterisk: 'red-asterisk' },
              }}
              InputProps={{
                style: { color: 'grey', fontSize: '15px' },
              }}
              required
              disabled={submitting}
              error={!!errors[`contact-${contact.id}`]}
              helperText={errors[`contact-${contact.id}`]}
            />
            <TextField
              name="email"
              label="Email ID"
              type="email"
              variant="outlined"
              fullWidth
              value={contact.email}
              onChange={(e) => handleCustomerChange(contact.id, 'email', e.target.value)}
              placeholder="Enter email (e.g., john.doe@example.com)"
              InputLabelProps={{
                shrink: true,
                style: { color: 'black' },
                required: true,
                classes: { asterisk: 'red-asterisk' },
              }}
              InputProps={{
                style: { color: 'grey', fontSize: '15px' },
              }}
              required
              disabled={submitting}
              error={!!errors[`email-${contact.id}`]}
              helperText={errors[`email-${contact.id}`]}
            />
            <Button
              style={{ background: 'none' }}
              className="Project-remove-contact-button"
              onClick={() => handleRemoveContact(contact.id)}
              disabled={submitting || formData.customerContacts.length === 1}
            >
              <img src={DeleteIcon} alt="Delete" className="Project-action-icon" />
            </Button>
          </div>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            No contacts added. Click "Add New" to add a contact.
          </Typography>
        )}
        <Button
          className="Project-add-new-button"
          onClick={handleAddNew}
          disabled={submitting}
          sx={{
            background: 'none !important',
            border: 'none !important',
            '&:hover': {
              background: 'none',
              opacity: 0.8,
            },
          }}
        >
          <img src={AddIcon} alt="Add Icon" className="Project-add-icon" /> Add New
        </Button>
      </DialogContent>

      <DialogActions className="Project-dialog-actions">
        <Button
          onClick={onClose}
          className="Project-cancel-button"
          disabled={submitting}
          sx={{
            background: 'none',
            border: '1px solid #013E7D',
            color: '#0066D1',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 16px',
            '&:hover': {
              background: 'none',
              opacity: 0.8,
            },
            '&.Mui-disabled': {
              color: '#cccccc',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="Project-save-button"
          disabled={
            submitting ||
            !hasChanges ||
            !formData.projectName ||
            !formData.projectStatus ||
            !formData.projectType ||
            !formData.projectStartDate ||
            !formData.projectEndDate ||
            !formData.projectPractice ||
            !formData.projectClient ||
            formData.customerContacts.length === 0 ||
            formData.customerContacts.some((c) => !c.fullName || !c.email) ||
            formData.customerContacts.some((c, idx) => contactErrors[idx]?.fullName || contactErrors[idx]?.email || contactErrors[idx]?.duplicate) ||
            !!nameError ||
            !!startDateError ||
            !!endDateError ||
            loadingClients
          }
          sx={{
            background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%)',
            color: 'white',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 32px',
            borderRadius: '8px',
            border: 'none',
            '&:hover': {
              background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%)',
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              background: '#cccccc',
              color: '#666666',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditDialog.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Status: PropTypes.string,
    Type: PropTypes.string,
    'Start Date': PropTypes.string,
    'End Date': PropTypes.string,
    'Project Name': PropTypes.string,
    Description: PropTypes.string,
    FullName: PropTypes.string,
    Email: PropTypes.string,
    Practice: PropTypes.string,
    Client: PropTypes.string,
  }),
  statuses: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Name: PropTypes.string,
      }),
    ])
  ).isRequired,
  types: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Name: PropTypes.string,
      }),
    ])
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditDialog;